import { Loader, MessagePanel } from '@portal/components'
import { useDittoAppConnection } from 'components/dittoAppConnection'
import { useDittoCloudConnectionStatus } from 'components/hooks'
import { useAppToDatabaseRename } from 'components/hooks'
import React, { useMemo } from 'react'

import useCurrentApp from '../hooks/useCurrentApp'
import IssuesManagementProvider from './client'
import DittoIssuesContext from './context'

type Props = {
  // App ID
  appId: string
  children: React.ReactNode
}

export const DITTO_PORTAL_APP = '5abd39a3-7af1-4ca8-8d86-27daf60094c3'

/** The Ditto store context provider. */
const DittoIssuesProvider: React.FC<Props> = ({ children, appId }) => {
  const { databaseText } = useAppToDatabaseRename()
  /** This would resolve promise with Suspense support */
  const { user, organizationId } = useCurrentApp(appId)
  const { error, ditto } = useDittoAppConnection(DITTO_PORTAL_APP)
  const isConnected = useDittoCloudConnectionStatus({ path: appId })

  const client = useMemo(() => {
    if (!ditto || !isConnected) {
      return null
    }

    return new IssuesManagementProvider(
      ditto.store,
      {
        id: user.id,
        email: user.email,
      },
      { organizationId, appId },
    )
  }, [ditto, user, organizationId, appId, isConnected])

  const renderContents = () => {
    if (!ditto || !isConnected) {
      return (
        <div
          className="flex items-center justify-center"
          data-testid="loaderWrapper"
        >
          <Loader className="mt-20" />
        </div>
      )
    }

    return children
  }

  return (
    <DittoIssuesContext.Provider value={{ client: client! }}>
      {!!error && (
        <MessagePanel
          variant="danger"
          message={
            error
              ? error.message
              : `Ooops! There was an error connecting to the Ditto ${databaseText}.`
          }
        />
      )}
      {renderContents()}
    </DittoIssuesContext.Provider>
  )
}

export default DittoIssuesProvider
